/**
 * Club content, with the fallback rule made visible.
 *
 * CLAUDE.md: show the original with a small note, never an empty field,
 * never machine translation. The note is not optional styling — a member
 * reading a programme description in a language they did not choose is owed
 * an explanation of why.
 */
import type { Localised } from "../lib/api";

export function LocalisedField({
  content,
  field,
  as: Tag = "p",
  className,
  note = false,
}: {
  content: Localised | null | undefined;
  field: string;
  as?: "p" | "h1" | "h2" | "h3" | "span" | "div";
  className?: string;
  /**
   * Whether to print the fallback note under this field.
   *
   * Off by default, and deliberately so. A programme's name, description and
   * performers all come from the same row in the same language, so a note
   * per field says the same sentence three times on one screen and stops
   * reading as information. Set it on ONE field per screen — normally the
   * title — or use `<FallbackNote>` once, wherever it belongs in the layout.
   */
  note?: boolean;
}) {
  // No text in any locale is a content bug, not a display case. Rendering
  // nothing is better than rendering "undefined" at a member.
  if (!content) return null;
  const value = content[field];
  if (typeof value !== "string" || !value.trim()) return null;

  return (
    <>
      <Tag className={className} lang={content.locale}>
        {value}
      </Tag>
      {note && <FallbackNote content={content} />}
    </>
  );
}

/** The note on its own, for screens that place it once rather than per field. */
export function FallbackNote({ content }: { content: Localised | null | undefined }) {
  if (!content?.is_fallback || !content.fallback_note) return null;
  return <p className="fallback-note">{content.fallback_note}</p>;
}

/** The name on its own, for cards and lists where the note appears once for
 *  the whole card rather than under every field. */
export function localisedValue(content: Localised | null | undefined, field: string): string {
  if (!content) return "";
  const value = content[field];
  return typeof value === "string" ? value : "";
}
